import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Pause, Play } from "lucide-react";
import { cn } from "@/lib/utils";
import { MidiaAutenticada } from "../../referencias/presentation/midia-autenticada";

export type SlidePreviewMidia = {
  readonly chave: string;
  readonly url: string;
  readonly ehVideo: boolean;
};

type TipoVisual = "imagem" | "carrossel" | "reels";

type Props = {
  readonly arquivos?: readonly File[];
  readonly slides?: readonly SlidePreviewMidia[];
  readonly tipo?: TipoVisual;
  /** Dentro de outro container (palco do modal): ocupa 100% dele. */
  readonly incorporado?: boolean;
};

/**
 * Stage de preview ao lado do modal: so a midia.
 * Carrossel com setas; reels com play/pause.
 */
export function PreviewMidiasAgendamento({
  arquivos,
  slides: slidesProp,
  tipo = "imagem",
  incorporado = false,
}: Props) {
  const [slidesLocais, setSlidesLocais] = useState<readonly SlidePreviewMidia[]>(
    [],
  );
  const [indice, setIndice] = useState(0);
  const [tocando, setTocando] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (!arquivos || arquivos.length === 0) {
      setSlidesLocais([]);
      return;
    }
    const gerados = arquivos.map((arquivo, i) => ({
      chave: `${arquivo.name}-${arquivo.size}-${arquivo.lastModified}-${i}`,
      url: URL.createObjectURL(arquivo),
      ehVideo: arquivo.type.startsWith("video/"),
    }));
    setSlidesLocais(gerados);
    setIndice(0);
    setTocando(false);
    return () => {
      for (const p of gerados) URL.revokeObjectURL(p.url);
    };
  }, [arquivos]);

  const previews =
    slidesProp && slidesProp.length > 0 ? slidesProp : slidesLocais;

  useEffect(() => {
    setIndice(0);
    setTocando(false);
  }, [slidesProp]);

  const atual = previews[Math.min(indice, Math.max(previews.length - 1, 0))];
  const usaProxy = Boolean(atual?.url.startsWith("/api/"));

  useEffect(() => {
    if (usaProxy) return;
    const video = videoRef.current;
    if (!video) return;
    if (tocando) {
      void video.play().catch(() => setTocando(false));
      return;
    }
    try {
      video.pause();
    } catch {
      // jsdom nao implementa pause
    }
  }, [tocando, indice, previews, usaProxy]);

  if (previews.length === 0 || !atual) return null;

  const ehCarrossel = tipo === "carrossel" || previews.length > 1;
  const ehReels = tipo === "reels" || (previews.length === 1 && atual.ehVideo);

  function ir(delta: number) {
    setIndice((i) => {
      const total = previews.length;
      return (i + delta + total) % total;
    });
    setTocando(false);
  }

  function alternarPlay() {
    setTocando((v) => !v);
  }

  return (
    <div
      className={cn(
        "relative overflow-hidden bg-black",
        incorporado
          ? "size-full"
          : cn(
              "rounded-2xl border border-border-subtle shadow-e3",
              ehReels
                ? "aspect-[9/16] max-h-[min(80vh,720px)]"
                : "aspect-[4/5] max-h-[min(80vh,640px)]",
            ),
      )}
      aria-label="Prévia da mídia"
    >
      {usaProxy ? (
        <MidiaAutenticada
          key={atual.chave}
          src={atual.url}
          tipo={atual.ehVideo ? "video" : "image"}
          controls={atual.ehVideo}
          className={cn(
            "size-full object-contain",
            atual.ehVideo && "bg-black",
          )}
        />
      ) : atual.ehVideo ? (
        <video
          key={atual.chave}
          ref={videoRef}
          src={atual.url}
          className="size-full object-contain bg-black"
          playsInline
          preload="metadata"
          loop={ehReels}
          onEnded={() => setTocando(false)}
          aria-label={`Vídeo ${indice + 1}`}
          onClick={ehReels ? alternarPlay : undefined}
        />
      ) : (
        <img
          key={atual.chave}
          src={atual.url}
          alt=""
          className="size-full object-contain"
        />
      )}

      {!usaProxy && ehReels && atual.ehVideo ? (
        <button
          type="button"
          onClick={alternarPlay}
          aria-label={tocando ? "Pausar preview" : "Reproduzir preview"}
          className={cn(
            "absolute inset-0 grid cursor-pointer place-items-center",
            "bg-transparent transition-colors duration-150",
            !tocando && "bg-black/25",
          )}
        >
          <span
            className={cn(
              "grid size-14 place-items-center rounded-full bg-black/55 text-white ring-1 ring-white/20 backdrop-blur-md",
              "transition-transform duration-150 active:scale-[0.97]",
              tocando &&
                "opacity-0 hover:opacity-100 focus-visible:opacity-100",
            )}
          >
            {tocando ? (
              <Pause className="size-6" aria-hidden />
            ) : (
              <Play className="size-6 translate-x-0.5" aria-hidden />
            )}
          </span>
        </button>
      ) : null}

      {ehCarrossel && previews.length > 1 ? (
        <>
          <button
            type="button"
            aria-label="Mídia anterior"
            onClick={() => ir(-1)}
            className="absolute top-1/2 left-2 grid size-9 -translate-y-1/2 cursor-pointer place-items-center rounded-full bg-background/90 text-foreground shadow-sm ring-1 ring-border transition-transform duration-150 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ChevronLeft className="size-5" aria-hidden />
          </button>
          <button
            type="button"
            aria-label="Próxima mídia"
            onClick={() => ir(1)}
            className="absolute top-1/2 right-2 grid size-9 -translate-y-1/2 cursor-pointer place-items-center rounded-full bg-background/90 text-foreground shadow-sm ring-1 ring-border transition-transform duration-150 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <ChevronRight className="size-5" aria-hidden />
          </button>
          <div className="absolute bottom-3 inset-x-0 flex justify-center gap-1.5">
            {previews.map((p, i) => (
              <button
                key={p.chave}
                type="button"
                aria-label={`Ir para mídia ${i + 1}`}
                aria-current={i === indice ? "true" : undefined}
                onClick={() => {
                  setIndice(i);
                  setTocando(false);
                }}
                className={cn(
                  "size-2 cursor-pointer rounded-full transition-colors duration-150",
                  i === indice
                    ? "bg-primary"
                    : "bg-background/70 ring-1 ring-border",
                )}
              />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
